import React, { FC } from 'react';
import { useButton, useDeviceInfo, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import { ModesWrapper, NoLicensesWrapper } from '~Scenes/Gateway/styles';
import { CompanyModules } from '~Scenes/Gateway/types';
import { BlueButton, Box } from '~Styles';
import ModeSkeleton from '../../ModeSkeleton';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import LicenseIcon from '~Assets/icons/license.svg';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useStorage } from '~Components/Storage';
import Mode from '../../Mode';
import DeviceIconSrc from '~Assets/icons/devices.svg';
import ExpressIconSrc from '~Assets/icons/product_express-checkout.svg';
import KDSIconSrc from '~Assets/icons/product_kitchen-display.svg';
import ProductORDSrc from '~Assets/icons/product_order-ready-display.svg';
import QSRIconSrc from '~Assets/icons/pos.svg';
import MultiQSRIconSrc from '~Assets/icons/devices.svg';
import TSRIconSrc from '~Assets/icons/tsr.svg';
import HandheldIconSrc from '~Assets/icons/handheld.svg';
import InventoryIcon from '~Assets/icons/boxes_stacked_gray.svg';
import POSBackgroundSrc from '~Assets/Images/Gateway/pos.webp';
import TSRBackgroundSrc from '~Assets/Images/Gateway/tsr.webp';
import ExpressBackgroundSrc from '~Assets/Images/Gateway/express.webp';
import CDSVerticalBackgroundSrc from '~Assets/Images/Gateway/customer-display-vertical.webp';
import CDSHorizontalBackgroundSrc from '~Assets/Images/Gateway/customer-display-horizontal.webp';
import KDSBackgroundSrc from '~Assets/Images/Gateway/kds.webp';
import HandheldBackgroundSrc from '~Assets/Images/Gateway/handheld.webp';

interface Props {
  companyModules: CompanyModules;
  noLicensesAvailable: boolean;
  venueId: string;
  toggleChangeDeviceModal: () => void;
}

/** The content rendered on the screen besides the news */
const ModesContent: FC<Props> = ({
  companyModules,
  noLicensesAvailable,
  venueId,
  toggleChangeDeviceModal,
}) => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const logger = useLogger('gateway');
  const deviceHandler = useDeviceHandler();
  const deviceInfo = useDeviceInfo();
  const [storage] = useStorage();

  const companyFlags = useSelector((state) => state.companyFlags);

  const changeDeviceButton = useButton({
    onClick: () => {
      logger.info('Change device modal opened in Gateway');
      toggleChangeDeviceModal();
    },
  });

  const hasDeviceClaimed = companyFlags.useNewLicenses
    ? Boolean(deviceHandler.activeDevice?.id)
    : Boolean(storage.taxComplianceDeviceId);

  // Animation delay counter for staggered entrance
  let animationIndex = 0;
  const getAnimationDelay = () => {
    const delay = animationIndex * 50;
    animationIndex++;
    return delay;
  };

  if (companyFlags.useNewLicenses && licenses.loading) {
    return (
      <ModesWrapper display="flex" flexDirection="column" gap="small" marginTop="medium">
        <Box display="flex" gap="small">
          <ModeSkeleton animationDelay={0} />
          <ModeSkeleton animationDelay={50} />
        </Box>
        <ModeSkeleton animationDelay={100} />
        <ModeSkeleton animationDelay={150} />
      </ModesWrapper>
    );
  }

  if (noLicensesAvailable) {
    return (
      <NoLicensesWrapper
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        marginTop="3xl">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="xs"
          justifyContent="center">
          <Icon height="64px" width="64px" src={LicenseIcon} color="var(--colorBlueGray200)" />

          <Heading level={2} color="var(--colorWhite)">
            {i18n('Gateway.NoLicensesAvailable')}
          </Heading>
          <Paragraph style={{ textAlign: 'center' }} level={4} color="var(--colorBlueGray200)">
            {i18n('Gateway.NoLicensesAvailable.Desc')}
          </Paragraph>

          <BlueButton marginTop="small" {...changeDeviceButton}>
            {i18n('ClaimDevice.ChangeDevice')}
          </BlueButton>
        </Box>
      </NoLicensesWrapper>
    );
  }

  if (!hasDeviceClaimed) {
    return (
      <NoLicensesWrapper
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        marginTop="3xl">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="xs"
          justifyContent="center">
          <Icon height="64px" width="64px" src={DeviceIconSrc} color="var(--colorBlueGray200)" />

          <Heading level={2} color="var(--colorWhite)">
            {i18n('ClaimDevice.RegisterDevice')}
          </Heading>

          <Paragraph style={{ textAlign: 'center' }} level={4} color="var(--colorBlueGray200)">
            {i18n('ClaimDevice.RegisterDevice.ClaimText')}
          </Paragraph>

          <BlueButton marginTop="small" {...changeDeviceButton}>
            {i18n('ClaimDevice.ClaimFirstDevice')}
          </BlueButton>
        </Box>
      </NoLicensesWrapper>
    );
  }

  return (
    <ModesWrapper display="flex" flexDirection="column" gap="small" marginTop="medium">
      {(companyModules.POS.active || companyModules.POS.claimable) && (
        <Box display="flex" gap="small">
          <Mode
            to={`/venue/${venueId}/pos`}
            text={i18n('Gateway.PointOfSale')}
            isClaimable={companyModules.POS.claimable}
            iconSrc={QSRIconSrc}
            backgroundImageSrc={POSBackgroundSrc}
            animationDelay={getAnimationDelay()}
          />
          <Mode
            to={`/venue/${venueId}/multi-pos`}
            text={i18n('Gateway.PointOfSaleMulti')}
            isClaimable={companyModules.POS.claimable}
            iconSrc={MultiQSRIconSrc}
            backgroundImageSrc={POSBackgroundSrc}
            animationDelay={getAnimationDelay()}
          />
        </Box>
      )}
      {(companyModules.tables.active || companyModules.tables.claimable) && (
        <Mode
          to={`/venue/${venueId}/tables`}
          text={i18n('Gateway.TableManagement')}
          isClaimable={companyModules.tables.claimable}
          iconSrc={TSRIconSrc}
          backgroundImageSrc={TSRBackgroundSrc}
          animationDelay={getAnimationDelay()}
        />
      )}
      {(companyModules.KDS.active || companyModules.KDS.claimable) && (
        <Mode
          to={`/venue/${venueId}/evented-kitchen-display`}
          text={i18n('Gateway.KitchenDisplay')}
          isClaimable={companyModules.KDS.claimable}
          iconSrc={KDSIconSrc}
          backgroundImageSrc={KDSBackgroundSrc}
          animationDelay={getAnimationDelay()}
        />
      )}
      {(companyModules.CDS.active || companyModules.CDS.claimable) && (
        <Box display="flex" gap="small">
          <Mode
            to={`/venue/${venueId}/customer-display`}
            text={i18n('Gateway.CustomerDisplay')}
            isClaimable={companyModules.CDS.claimable}
            iconSrc={ProductORDSrc}
            backgroundImageSrc={CDSVerticalBackgroundSrc}
            animationDelay={getAnimationDelay()}
          />
          <Mode
            to={`/venue/${venueId}/customer-display-responsive`}
            text={i18n('Gateway.CustomerDisplayLarge')}
            isClaimable={companyModules.CDS.claimable}
            iconSrc={ProductORDSrc}
            backgroundImageSrc={CDSHorizontalBackgroundSrc}
            animationDelay={getAnimationDelay()}
          />
        </Box>
      )}
      {(companyModules.express.active || companyModules.express.claimable) && (
        <Box display="flex" gap="small">
          <Mode
            to={`/venue/${venueId}/self-service-kiosks`}
            text={i18n('Gateway.SelfServiceKiosk')}
            isClaimable={companyModules.express.claimable}
            iconSrc={ExpressIconSrc}
            backgroundImageSrc={ExpressBackgroundSrc}
            animationDelay={getAnimationDelay()}
          />
          {(companyModules.epos2.active || companyModules.epos2.claimable) && (
            <Mode
              to={`/venue/${venueId}/${companyModules.epos2.path}`}
              text={i18n('Gateway.EPOS2')}
              isClaimable={companyModules.epos2.claimable}
              iconSrc={ExpressIconSrc}
              backgroundImageSrc={ExpressBackgroundSrc}
              animationDelay={getAnimationDelay()}
            />
          )}
        </Box>
      )}
      {(companyModules.inventory.active || companyModules.inventory.claimable) && (
        <Mode
          to={`/venue/${venueId}/inventory`}
          text={i18n('Gateway.Inventory')}
          isClaimable={companyModules.inventory.claimable}
          iconSrc={InventoryIcon}
          animationDelay={getAnimationDelay()}
        />
      )}
      {(deviceInfo?.isHandheldTerminal || window.__DEV__) && (
        <Mode
          to={`/venue/${venueId}/handy`}
          text={i18n('Handy.Title')}
          isClaimable={false}
          iconSrc={HandheldIconSrc}
          backgroundImageSrc={HandheldBackgroundSrc}
          animationDelay={getAnimationDelay()}
        />
      )}
    </ModesWrapper>
  );
};

export default ModesContent;
